import React, { useState, useEffect } from 'react';
import axiosInstance from '../AxiosInstance';
import { Container, TextField, Button, Box, Typography, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useParams } from 'react-router-dom';

const ReconcileInventory = () => {
  const { id } = useParams(); // Extract inventory ID from URL
  const [inventory, setInventory] = useState({});
  const [actualStock, setActualStock] = useState(''); // For entering the actual stock
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [productUnits, setProductUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [currentStockForUnit, setCurrentStockForUnit] = useState('');

  useEffect(() => {
    const fetchInventoryAndUnits = async () => {
      try {
        const response = await axiosInstance.get(`/inventories/${id}`); // Fetch the inventory using the ID
        const inventoryData = response.data;
        setInventory(inventoryData);
        
        // Fetch reorder candidates to get enriched unit data with currentStock
        const reorderResponse = await axiosInstance.get('/inventories/reorder-candidates');
        const reorderCandidates = reorderResponse.data.reorderCandidates || [];
        
        // Find the matching product in reorder candidates
        const matchingCandidate = reorderCandidates.find(candidate => 
          candidate.productId === inventoryData.product_id
        );
        
        if (matchingCandidate && matchingCandidate.unitTypes) {
          // Get basic units info for unit_category
          const basicUnitsResponse = await axiosInstance.get(`/units/product/${inventoryData.product_id}/unitInfo`);
          const basicUnits = basicUnitsResponse.data;
          
          // Merge unitTypes with unit_category info
          let mergedUnits = matchingCandidate.unitTypes.map(unitType => {
            const basicUnit = basicUnits.find(bu => bu.unit_id === unitType.unitId);
            return {
              ...unitType,
              unit_category: basicUnit ? basicUnit.unit_category : 'selling'
            };
          });

          // Ensure the current inventory unit is included
          const currentUnitIncluded = mergedUnits.some(unit => unit.unitId === inventoryData.unit_id);
          if (!currentUnitIncluded) {
            const currentBasicUnit = basicUnits.find(bu => bu.unit_id === inventoryData.unit_id);
            mergedUnits.push({
              unitId: inventoryData.unit_id,
              unitType: inventoryData.unit_type,
              currentStock: parseFloat(inventoryData.current_stock),
              unit_category: currentBasicUnit ? currentBasicUnit.unit_category : 'selling'
            });
          }
          
          setProductUnits(mergedUnits);
          
          // Find the current unit in unitTypes to get its currentStock
          const currentUnit = mergedUnits.find(unit => 
            unit.unitId === inventoryData.unit_id
          );
          
          setSelectedUnitId(inventoryData.unit_id);
          setCurrentStockForUnit(currentUnit ? currentUnit.currentStock.toFixed(2) : parseFloat(inventoryData.current_stock).toFixed(2));
        } else {
          // Fallback to basic units data
          const unitsResponse = await axiosInstance.get(`/units/product/${inventoryData.product_id}/unitInfo`);
          setProductUnits(unitsResponse.data.map(unit => ({
            ...unit,
            unitId: unit.unit_id,
            unitType: unit.unit_type,
            currentStock: unit.unit_id === inventoryData.unit_id ? parseFloat(inventoryData.current_stock) : 0
          })));
          
          setSelectedUnitId(inventoryData.unit_id);
          setCurrentStockForUnit(parseFloat(inventoryData.current_stock).toFixed(2));
        }
      } catch (error) {
        console.error('Error fetching inventory or units:', error);
        setSnackbarMessage('Error fetching inventory');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    fetchInventoryAndUnits();
  }, [id]);

  const handleUnitChange = (unitId) => {
    setSelectedUnitId(unitId);
    
    // Use the same logic as ViewInventory - find currentStock in unitTypes data
    const selectedUnit = productUnits.find(unit => unit.unitId === unitId);
    const stockValue = selectedUnit && typeof selectedUnit.currentStock === 'number' 
      ? selectedUnit.currentStock.toFixed(2)
      : parseFloat(inventory.current_stock).toFixed(2);
    
    setCurrentStockForUnit(stockValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNaN(actualStock) || actualStock === '' || parseFloat(actualStock) < 0) {
      setSnackbarMessage('Please enter a valid positive stock number (negative values not allowed)');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await axiosInstance.post(`/inventories/reconcile/${id}`, {
        actual_stock: actualStock,
        actual_unit_id: selectedUnitId,
      });
      setSnackbarMessage('Inventory reconciled successfully!');
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Error reconciling inventory:', error);
      setSnackbarMessage('Error reconciling inventory');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Reconcile Inventory
        </Typography>
        <form onSubmit={handleSubmit}>
          {/* Product Information */}
          <TextField
            label="Product Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={`${inventory.product_name || ''}${inventory.brand ? ` (${inventory.brand})` : ''}${(inventory.variety || inventory.size) ? ` - ${[inventory.variety, inventory.size].filter(Boolean).join(', ')}` : ''}`}
            disabled
          />
          {/* Current Stock */}
          <TextField
            label="Current Stock"
            variant="outlined"
            fullWidth
            margin="normal"
            value={currentStockForUnit || ''}
            disabled
          />
          {/* Unit Type Dropdown */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Unit Type</InputLabel>
            <Select
              value={selectedUnitId}
              onChange={(e) => handleUnitChange(e.target.value)}
              label="Unit Type"
            >
              {productUnits.map((unit) => (
                <MenuItem key={unit.unitId} value={unit.unitId}>
                  {unit.unitType} ({unit.unit_category || 'selling'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Actual Stock to be reconciled */}
          <TextField
            label="Actual Stock"
            variant="outlined"
            fullWidth
            margin="normal"
            value={actualStock}
            onChange={(e) => setActualStock(e.target.value)}
            required
          />
          {/* Submit Button */}
          <Button type="submit" variant="contained" color="primary">
            Reconcile Inventory
          </Button>
        </form>
      </Box>
      {/* Snackbar to show success or error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReconcileInventory;